import { createContext, useContext, useMemo, type ReactNode } from "react";
import { toast, Toaster } from "sonner";
import { useTheme } from "../theme/ThemeProvider.js";

export type ToastType = "info" | "success" | "warning" | "error";

export interface ToastApi {
  show: (content: ReactNode, type?: ToastType, ms?: number) => void;
  info: (content: ReactNode) => void;
  success: (content: ReactNode) => void;
  warning: (content: ReactNode) => void;
  error: (content: ReactNode) => void;
}

export type NotificationPlacement = "topRight" | "topLeft" | "bottomRight" | "bottomLeft";

export interface NotificationConfig {
  type?: ToastType;
  title?: ReactNode;
  description?: ReactNode;
  placement?: NotificationPlacement;
  duration?: number;
}

export interface NotificationApi {
  open: (config: NotificationConfig) => void;
  info: (title: ReactNode, description?: ReactNode) => void;
  success: (title: ReactNode, description?: ReactNode) => void;
  warning: (title: ReactNode, description?: ReactNode) => void;
  error: (title: ReactNode, description?: ReactNode) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const NotificationContext = createContext<NotificationApi | null>(null);

// sonner's toast fns keyed by our semantic type (message = the neutral variant).
const emit = {
  info: toast.info,
  success: toast.success,
  warning: toast.warning,
  error: toast.error,
} as const;

/**
 * Toast + notification API, backed by sonner. The public ToastApi/NotificationApi
 * surface is unchanged, so callers keep using `useToast()` / `useNotification()`
 * exactly as before — only the engine swapped from antd's message/notification to
 * sonner. The <Toaster/> is rendered once here, theme-synced to <ThemeProvider>.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { theme } = useTheme();

  const toastApi = useMemo<ToastApi>(() => {
    const show = (content: ReactNode, type: ToastType = "info", ms = 3000) =>
      emit[type](content, { duration: ms });
    return {
      show,
      info: (c) => show(c, "info"),
      success: (c) => show(c, "success"),
      warning: (c) => show(c, "warning"),
      error: (c) => show(c, "error"),
    };
  }, []);

  const notifApi = useMemo<NotificationApi>(() => {
    const open = (c: NotificationConfig) => {
      const type = c.type ?? "info";
      emit[type](c.title, {
        ...(c.description != null ? { description: c.description } : {}),
        ...(c.duration !== undefined ? { duration: c.duration } : {}),
      });
    };
    return {
      open,
      info: (title, description) => open({ type: "info", title, description }),
      success: (title, description) => open({ type: "success", title, description }),
      warning: (title, description) => open({ type: "warning", title, description }),
      error: (title, description) => open({ type: "error", title, description }),
    };
  }, []);

  return (
    <ToastContext.Provider value={toastApi}>
      <NotificationContext.Provider value={notifApi}>
        {children}
        <Toaster theme={theme} position="top-right" richColors closeButton />
      </NotificationContext.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export function useNotification(): NotificationApi {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within <ToastProvider>");
  return ctx;
}
